import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { buildDateFormatter } from '~/platform/shared/claims-appeals';
import AddFilesForm from '../claim-files-tab/AddFilesForm';
import RequestNotifications from './shared/RequestNotifications';
import { formatRespondedDateTime } from '../../utils/helpers';
import { recordRequestSummaryBoxEvent } from '../../utils/analytics';
import {
  getNewestDocument,
  hasSupportingDocuments,
} from '../../utils/trackedItemProcessing';

const RespondedOrPastDueRequestPage = props => {
  const {
    acceptedFileTypes,
    documentTypes,
    item,
    message,
    onCancel,
    onSubmit,
    progress,
    requestContent,
    nextStepsContent,
    pageTitle,
    requestText,
    showUploadForm,
    summaryBoxList,
    type1UnknownErrors,
    uploadResults,
    uploading,
    useVaTabs = false,
  } = props;

  const dateFormatter = buildDateFormatter();

  // Without documents, the item is on this page because its suspense date passed
  const hasDocuments = hasSupportingDocuments(item);

  useEffect(() => {
    recordRequestSummaryBoxEvent({
      heading: hasDocuments ? 'Responded' : 'Past due',
    });
  }, [hasDocuments, item.id]);

  const getRequestFulfilledSummaryBox = () => {
    const { uploadDate, uploadDateTime } = getNewestDocument(item) || {};
    const respondedDate =
      (uploadDateTime && formatRespondedDateTime(uploadDateTime)) ||
      (uploadDate && dateFormatter(uploadDate));
    const header = respondedDate
      ? `You responded to this request on ${respondedDate}`
      : 'You responded to this request';

    return (
      <va-summary-box>
        <h2
          className="vads-u-margin-top--0 vads-u-font-size--h3"
          slot="headline"
        >
          {header}
        </h2>
        <p>
          While we review the evidence you submitted, you can still take these
          actions:
        </p>
        {summaryBoxList}
      </va-summary-box>
    );
  };

  const pastDueSummaryBox = (
    <va-summary-box>
      <h2 className="vads-u-margin-top--0 vads-u-font-size--h3" slot="headline">
        The deadline to respond to this request has passed
      </h2>
      <p>
        <strong>You can still upload documents.</strong> Sending the information
        we asked for could still help. But your claim may be delayed while we
        review it.
      </p>
      <p>
        <strong>Note:</strong> We can’t track files you submitted by mail, in
        person, or QuickSubmit here. If you’ve already submitted documents using
        one of these methods, you can ignore this message.
      </p>
    </va-summary-box>
  );

  return (
    <div id="default-page" className="vads-u-margin-bottom--3">
      <div className="vads-u-margin-bottom--4">
        <h1 className="claims-header">
          {pageTitle}
          <span className="vads-u-font-family--sans vads-u-margin-top--0p5">
            {requestText}
          </span>
        </h1>
      </div>
      <RequestNotifications
        message={message}
        trackedItem={item}
        type1UnknownErrors={type1UnknownErrors}
        useVaTabs={useVaTabs}
      />
      {hasDocuments ? getRequestFulfilledSummaryBox() : pastDueSummaryBox}
      {showUploadForm && (
        <AddFilesForm
          useVaTabs={useVaTabs}
          acceptedFileTypes={acceptedFileTypes}
          documentTypes={documentTypes}
          progress={progress}
          uploadResults={uploadResults}
          uploading={uploading}
          onCancel={onCancel}
          onSubmit={files => onSubmit(files)}
        />
      )}
      <div className={showUploadForm ? undefined : 'vads-u-margin-top--4'}>
        <va-accordion bordered open-single>
          <va-accordion-item
            header={
              hasDocuments ? 'The request you responded to' : 'Original request'
            }
            open
          >
            <h3 className="vads-u-margin-top--0 vads-u-margin-bottom--4">
              {pageTitle}
            </h3>
            <h4 className="vads-u-margin-top--2 vads-u-margin-bottom--2">
              What we need from you
            </h4>
            {requestContent}
            {nextStepsContent}
          </va-accordion-item>
        </va-accordion>
      </div>
    </div>
  );
};

RespondedOrPastDueRequestPage.propTypes = {
  item: PropTypes.object.isRequired,
  pageTitle: PropTypes.string.isRequired,
  requestText: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  acceptedFileTypes: PropTypes.array,
  claimId: PropTypes.string,
  documentTypes: PropTypes.array,
  message: PropTypes.object,
  nextStepsContent: PropTypes.node,
  progress: PropTypes.number,
  requestContent: PropTypes.node,
  showUploadForm: PropTypes.bool,
  summaryBoxList: PropTypes.node,
  type1UnknownErrors: PropTypes.array,
  uploadResults: PropTypes.object,
  uploading: PropTypes.bool,
  useVaTabs: PropTypes.bool,
};

export default RespondedOrPastDueRequestPage;
